/**
 * The opened question: full decision view.
 *
 * Context first, then the options; below it the linked items (collapsible),
 * the person tags, and the lifecycle line Open → Answered → Recorded.
 * Top right: the green run button — rework this question, flavored by the
 * area's mode (Rework / Refine / Challenge / Decide / Wrap up).
 */

import { useState } from "react";
import type { Item } from "../lib/item";
import { personLabel, type Person } from "../lib/persons";
import {
  splitFrontmatter,
  statusWord,
  type AnswerPatch,
  type Question,
} from "../lib/questions";
import { renderMarkdown } from "../lib/markdown";
import { QuestionCard } from "./QuestionCard";
import { TagSheet } from "./TagSheet";

export function visibleBody(raw: string): string {
  const split = splitFrontmatter(raw);
  let body = split ? split.body : raw;
  body = body.replace(/<!--\s*ask:begin[^>]*-->[\s\S]*?<!--\s*ask:end\s*-->/g, "");
  body = body.replace(
    /<!--\s*answers:begin[^>]*-->[\s\S]*?<!--\s*answers:end\s*-->/g,
    "",
  );
  return body.trim();
}

function Lifecycle({ q }: { q: Question }) {
  const word = statusWord(q);
  const stages =
    word === "Never"
      ? ["Open", "Never"]
      : word === "Snoozed"
        ? ["Open", "Snoozed", "Answered", "Recorded"]
        : ["Open", "Answered", "Recorded"];
  if (word === "Superseded") {
    return <div className="mb-3 text-xs text-ink-500">Superseded</div>;
  }
  return (
    <div className="mb-3 flex flex-wrap items-center gap-1 text-xs">
      {stages.map((s, i) => (
        <span key={s} className="flex items-center gap-1">
          {i > 0 && <span className="text-ink-600">→</span>}
          <span className={s === word ? "font-semibold text-accent-soft" : "text-ink-500"}>
            {s}
          </span>
        </span>
      ))}
    </div>
  );
}

export function QuestionDetail({
  q,
  items,
  persons,
  busy,
  staged,
  runLabel,
  runDisabledReason,
  onRun,
  onReask,
  onAnswer,
  onSnooze,
  onSetPersons,
  onOpenItem,
  onBack,
}: {
  q: Question;
  items: Item[];
  persons: Person[];
  busy: boolean;
  staged: boolean;
  /** Green run button label (mode-aware). Omitted = no button. */
  runLabel?: string;
  /** When set, the run button is disabled and this is the reason (tooltip + hint). */
  runDisabledReason?: string;
  onRun?: () => void;
  /** Decided question: reset it to Open (staged). */
  onReask?: () => void;
  onAnswer: (patch: AnswerPatch) => void;
  onSnooze: (until: string) => void;
  onSetPersons: (ids: string[]) => void;
  onOpenItem: (item: Item) => void;
  onBack: () => void;
}) {
  const [sheetOpen, setSheetOpen] = useState(false);
  const linked = q.links
    .map((path) => items.find((i) => i.path === path))
    .filter((i): i is Item => i !== undefined);

  return (
    <div className="mx-auto w-full max-w-2xl px-4 pb-28 pt-4">
      <div className="mb-3 flex items-center justify-between gap-2">
        <button onClick={onBack} className="text-sm text-ink-400 hover:text-ink-200">
          ‹ Back
        </button>
        {q.status === "open"
          ? runLabel &&
            onRun && (
              <button
                className="btn-run shrink-0 px-3 py-1.5 text-xs"
                disabled={!!runDisabledReason}
                title={runDisabledReason ?? "Trigger a run for this question"}
                onClick={onRun}
              >
                {runLabel}
              </button>
            )
          : onReask && (
              <button
                className="btn-ghost shrink-0 px-3 py-1.5 text-xs"
                title="Reset this question to Open — answer it again"
                onClick={onReask}
              >
                ↺ Re-ask
              </button>
            )}
      </div>

      {q.kind === "consolidation" && (
        <div className="mb-2">
          <span className="chip bg-warn/15 text-warn">
            Consolidation approval · the plan card proposes, A/B/C decides
          </span>
        </div>
      )}

      <div className="mb-2 text-xs text-ink-500">
        created {q.created || "?"}
        {q.generatedBy ? ` · by ${q.generatedBy}` : ""}
      </div>

      <Lifecycle q={q} />

      <QuestionCard
        q={q}
        busy={busy}
        staged={staged}
        onAnswer={onAnswer}
        onSnooze={onSnooze}
      />

      <div className="mt-3 flex flex-wrap items-center gap-1.5">
        {q.persons.length === 0 ? (
          <span className="text-xs text-ink-500">No person tags</span>
        ) : (
          q.persons.map((p) => (
            <span key={p} className="chip bg-accent/10 text-accent-soft">
              {personLabel(persons, p) ?? p}
            </span>
          ))
        )}
        <button
          className="btn-ghost px-3 py-1 text-xs"
          onClick={() => setSheetOpen(true)}
        >
          Edit persons
        </button>
      </div>

      {linked.length > 0 && (
        <div className="mt-6">
          <div className="mb-2 text-xs font-medium uppercase tracking-wide text-ink-500">
            Items ({linked.length})
          </div>
          <div className="space-y-2">
            {linked.map((item) => (
              <details key={item.path} className="card overflow-hidden">
                <summary className="cursor-pointer list-none p-3 hover:bg-ink-850">
                  <span className="block text-sm font-medium text-ink-200">
                    {item.title}
                  </span>
                  {item.description && (
                    <span className="mt-1 block line-clamp-2 text-xs text-ink-500">
                      {item.description}
                    </span>
                  )}
                </summary>
                <div className="border-t border-ink-850 p-3">
                  <div
                    className="prose-item max-h-96 overflow-y-auto"
                    dangerouslySetInnerHTML={{
                      __html: renderMarkdown(visibleBody(item.raw)),
                    }}
                  />
                  <button
                    className="btn-ghost mt-2 w-full py-2 text-xs"
                    onClick={() => onOpenItem(item)}
                  >
                    Open the item
                  </button>
                </div>
              </details>
            ))}
          </div>
        </div>
      )}

      {sheetOpen && (
        <TagSheet
          persons={persons}
          selected={q.persons}
          onApply={(ids) => {
            setSheetOpen(false);
            onSetPersons(ids);
          }}
          onClose={() => setSheetOpen(false)}
        />
      )}
    </div>
  );
}
